import { useState } from 'react';
import { api } from './api';
import { text, type Locale } from '../shared/content';
import { errorText } from './i18n';
type Account = {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  closed: boolean;
  restricted: boolean;
  configuredAdmin: boolean;
  roles: ('ad_ops' | 'moderator')[];
};
const copy = {
  title: text('Operator roles', '运营岗位授权', '營運職務授權', 'Funciones de operadores'),
  info: text(
    'Full administrators are configured by the site owner. This tool only grants or revokes advertising and moderation roles for verified accounts. Roles do not grant game wealth or elected office.',
    '完整管理员由站主在部署配置中指定。这里仅可授予或撤销已验证账号的广告运营、内容审核岗位，不授予游戏财富或民选公职。',
    '完整管理員由站主於部署設定中指定。這裡僅可授予或撤銷已驗證帳號的廣告營運、內容審核職務，不授予遊戲財富或民選公職。',
    'Los administradores completos se configuran por el propietario. Aquí solo se conceden o revocan publicidad y moderación para cuentas verificadas, sin riqueza ni cargos electos.',
  ),
  query: text(
    'Exact email or account ID',
    '完整邮箱或账号 ID',
    '完整信箱或帳號 ID',
    'Correo exacto o ID de cuenta',
  ),
  search: text('Check account', '核对账号', '核對帳號', 'Comprobar cuenta'),
  missing: text('No matching account.', '未找到匹配账号。', '找不到相符帳號。', 'No se encontró la cuenta.'),
  verified: text('Email verified', '邮箱已验证', '信箱已驗證', 'Correo verificado'),
  unavailable: text(
    'Granting requires a verified, active and unrestricted account. Existing roles may still be revoked.',
    '授予岗位须账号已验证、未关闭且未受限；仍可撤销已有岗位。',
    '授予職務須帳號已驗證、未關閉且未受限；仍可撤銷既有職務。',
    'Para conceder funciones, la cuenta debe estar verificada, activa y sin restricciones. Las funciones existentes sí pueden revocarse.',
  ),
  configured: text(
    'Full administrator · deployment configuration',
    '完整管理员 · 来自部署配置',
    '完整管理員 · 來自部署設定',
    'Administrador completo · configuración del despliegue',
  ),
  ad_ops: text('Advertising operator', '广告运营', '廣告營運', 'Operador de publicidad'),
  moderator: text('Content moderator', '内容审核', '內容審核', 'Moderador de contenido'),
  adInfo: text(
    'Campaign business and reports; no content moderation or global switches.',
    '仅广告业务与报表，无内容审核和全局开关权限。',
    '僅廣告業務與報表，無內容審核及全域開關權限。',
    'Negocio publicitario e informes, sin moderación ni controles globales.',
  ),
  modInfo: text(
    'Reports, appeals and city articles; no advertising business or global switches.',
    '仅举报、申诉与城市文章审核，无广告业务和全局开关权限。',
    '僅檢舉、申訴及城市文章審核，無廣告業務及全域開關權限。',
    'Denuncias, apelaciones y artículos, sin negocio publicitario ni controles globales.',
  ),
  grant: text('Grant role', '授予岗位', '授予職務', 'Conceder función'),
  revoke: text('Revoke role', '撤销岗位', '撤銷職務', 'Revocar función'),
};
export default function StaffRolesPanel({
  locale,
  busy,
  onAction,
}: {
  locale: Locale;
  busy: boolean;
  onAction: (type: string, payload: Record<string, unknown>) => Promise<void>;
}) {
  const [query, setQuery] = useState('');
  const [account, setAccount] = useState<Account | null>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const t = (key: keyof typeof copy) => copy[key][locale];
  const lookup = async (input = query) => {
    setLoading(true);
    setError('');
    try {
      setAccount(
        (await api<{ account: Account | null }>(`/admin/staff?query=${encodeURIComponent(input)}`)).account,
      );
    } catch (e) {
      setAccount(undefined);
      setError(errorText(locale, e));
    } finally {
      setLoading(false);
    }
  };
  const canGrant = !!account?.emailVerified && !account.closed && !account.restricted;
  return (
    <section>
      <h3>{t('title')}</h3>
      <p className="muted small">{t('info')}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && !loading) void lookup();
        }}
      >
        <label>
          {t('query')}
          <input
            required
            minLength={3}
            maxLength={254}
            value={query}
            disabled={busy || loading}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setAccount(undefined);
            }}
          />
        </label>
        <button className="button soft full" disabled={busy || loading || query.trim().length < 3}>
          {t('search')}
        </button>
      </form>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {account === null && <p role="status">{t('missing')}</p>}
      {account && (
        <article className="admin-report">
          <strong>{account.name}</strong>
          <small style={{ overflowWrap: 'anywhere' }}>
            {account.email} · {account.id}
          </small>
          <p>{canGrant ? t('verified') : t('unavailable')}</p>
          {account.configuredAdmin && <p>{t('configured')}</p>}
          {(['ad_ops', 'moderator'] as const).map((role) => (
            <div key={role}>
              <h4>{t(role)}</h4>
              <p className="muted small">{t(role === 'ad_ops' ? 'adInfo' : 'modInfo')}</p>
              <button
                className="button soft full"
                disabled={busy || loading || (!canGrant && !account.roles.includes(role))}
                onClick={async () => {
                  const id = account.id;
                  setLoading(true);
                  try {
                    await onAction('staff.roles', {
                      userId: id,
                      role,
                      enabled: !account.roles.includes(role),
                    });
                    await lookup(id);
                  } catch (e) {
                    setError(errorText(locale, e));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {t(account.roles.includes(role) ? 'revoke' : 'grant')} · {t(role)}
              </button>
            </div>
          ))}
        </article>
      )}
    </section>
  );
}
